import $ from 'jquery'
import SFetch from '@app/utils/sfetch'

export default class BudgetTable {
  element: JQuery<HTMLElement>
  lockedPeriods: string[]

  constructor(element: HTMLElement) {
    this.element = $(element)
    this.lockedPeriods = [
      ...document.querySelectorAll('[data-behaviour="budget-lock"]'),
    ].reduce((result, el) => {
      const element = el as HTMLElement
      if (element.dataset.locked === 'true' && element.dataset.period) {
        result.push(element.dataset.period)
      }

      return result
    }, [] as string[])

    this.initListenerOnLedgerSelect()
    this.initListenerOnIndividualCells()
    this.initLock()
    this.initCalculateTotals()
    this.calculateTotals()
    this.initTooltipOnLargeAccountNames()
  }

  initListenerOnLedgerSelect(): void {
    const ledgerSelect = document.getElementById('budget-ledger-dropdown')

    if (ledgerSelect) {
      ledgerSelect.addEventListener('change', (el) =>
        this.changeLedger(el.target as HTMLSelectElement)
      )
    }
  }

  changeLedger(target: HTMLSelectElement): void {
    if (!target.value) {
      return
    }

    type ResponseRow = {
      elementId: string
      budgetType: string
      value: string
      url: string
    }

    SFetch.json<{ rows: ResponseRow[]; divider: string }>(target.value, {
      method: 'POST',
    })
      .then((response) => {
        if (!response) {
          return
        }

        const selectedPeriod: HTMLOptionElement | null =
          target.selectedOptions.item(0)

        if (selectedPeriod) {
          const selectedPeriodValue =
            selectedPeriod.attributes.getNamedItem('period')?.value
          const selectedPeriodText = selectedPeriod.text

          ;(
            document.querySelector(
              '[data-behaviour="budget-origin-period"]'
            ) as HTMLInputElement
          ).value = selectedPeriodValue || ''
          ;(
            document.getElementById('actuals-column-header') as HTMLElement
          ).innerText = `${selectedPeriodText} ${window.I18n.t(
            'js.report_period.relative.YTD'
          )}`
          ;(
            document.querySelector(
              '[data-behaviour="budget-divider"]'
            ) as HTMLInputElement
          ).value = response?.divider

          response.rows.forEach((row) => {
            let cell

            if (row.budgetType === 'account') {
              const accountRow = document.querySelector(
                `[data-budget-type="account"][data-element-id="${row.elementId}"]`
              )
              if (accountRow) {
                cell = accountRow.children[1].children[0]
                cell.textContent = row.value
                ;(cell as HTMLElement).dataset.url = row.url
              }
            } else {
              const accountRow = document.querySelector(
                `[data-budget-type="group"][data-element-id="${row.elementId}"]`
              )
              if (accountRow) {
                cell = accountRow.children[1]
                cell.textContent = row.value
              }
            }
          })
        }
      })
      .catch((e) => console.log(e))
  }

  initTooltipOnLargeAccountNames(): void {
    const accountNameCells = [
      ...document.querySelectorAll('tr td:first-child'),
    ] as HTMLElement[]

    accountNameCells.map((el) => {
      // If the scroll width is larger than the element width it means ellipsis were places through the css
      if (el.offsetWidth < el.scrollWidth) {
        el.dataset.toggle = 'tooltip'
        el.dataset.placement = 'bottom'
        el.title = el.innerText
      }
    })
  }

  initListenerOnIndividualCells(): void {
    this.element.on(
      'click',
      '[data-behaviour="budget-input"]',
      function (event) {
        const $target = $(event.target).closest('td')
        $target.find('input').focus()
      }
    )

    this.element.on(
      'focus',
      '[data-behaviour="budget-input"] input',
      function (event) {
        const $target = $(event.target)
        $($target).select()
      }
    )

    this.element.on(
      'change',
      '[data-behaviour="budget-input"] input',
      (event) => {
        $(event.target).addClass('budget-changed')
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        this.submitValues()
      }
    )
  }

  initLock(): void {
    this.click('[data-behaviour="budget-lock"]', async (event: Event) => {
      event.preventDefault()

      const $target: JQuery<Element> = $(event.target as Element).closest('a')
      let locked = false

      const clickedPeriod: string = $target.data('period') as string
      if ($.inArray(clickedPeriod, this.lockedPeriods) > -1) {
        this.lockedPeriods.splice(
          $.inArray(clickedPeriod, this.lockedPeriods),
          1
        )
      } else {
        this.lockedPeriods.push(clickedPeriod)
        locked = true
      }

      const href = $target.attr('href')
      if (href) {
        await $.post(href, () => {
          const $header = $target.closest('th')
          $(
            `[data-behaviour~='budget-table'] tr td:nth-child(${
              $header.index() + 1
            }) input`
          ).prop('disabled', locked)
          $(
            `[data-behaviour~='budget-table'] tr td:nth-child(${
              $header.index() + 1
            })`
          ).toggleClass('budget-locked')

          $(
            `[data-behaviour~='budget-table'] th [data-period~='${clickedPeriod}']`
          ).each(function (_index, headerElement) {
            $(headerElement).closest('th').toggleClass('budget-locked')
            $(headerElement).find('i').toggleClass('fa-unlock fa-lock muted')
          })
        })
      }
    })
  }

  initCalculateTotals(): void {
    document.addEventListener('calculate-budget-totals', () => {
      this.calculateTotals()
    })
  }

  calculateTotals(): void {
    if (document.querySelectorAll('.total-budget').length === 0) {
      return
    }

    ;[...document.querySelectorAll('tr[data-budget-type="account"]')].forEach(
      (element) => {
        this.updateTotal(element as HTMLElement)
      }
    )
  }

  updateTotal(row: HTMLElement): void {
    if (row.querySelectorAll('.total-budget').length === 0) return

    let totalValue = 0.0
    const rowInputs: Element[] = [...row.querySelectorAll('.budget-input')]

    rowInputs.forEach((inputElement) => {
      const centValue = (inputElement as HTMLInputElement).value.replace(
        /[^0-9-]/g,
        ''
      )
      const inputValue = parseFloat(centValue) / 100
      if (!isNaN(inputValue)) {
        totalValue += inputValue
      }
    })

    const budgetTotal = row.querySelector('.total-budget')
    if (budgetTotal) {
      budgetTotal.textContent = window.I18n.toNumber(totalValue, {
        precision: 2,
      })
    }
  }

  async submitValues(): Promise<void> {
    const changedFields: JQuery<HTMLElement> = this.element
      .find('input.budget-changed')
      .not('.budget-submitted')

    const rowsAffected = changedFields.map((_, element) => {
      return element.closest('tr')
    })
    const uniqRowsAffected = [...new Set(rowsAffected)]

    const hash = changedFields.map(function (index, element) {
      const $element = $(element)
      return { name: $element.attr('name'), value: $element.val() }
    })

    changedFields.addClass('budget-submitted')
    const action = this.element.closest('form').attr('action')

    if (action) {
      await $.post(action, hash, () => {
        changedFields.removeClass('budget-changed budget-submitted')
        changedFields.map((_, el) =>
          this.localizeBudgetValue(el as HTMLInputElement)
        )

        uniqRowsAffected.forEach((row) => {
          this.updateTotal(row)
        })
      })
    }
  }

  localizeBudgetValue(element: HTMLInputElement): void {
    if (element.value) {
      const elementValue = parseInt(element.value.replace(/[^0-9-]/g, '')) / 100
      element.value = window.I18n.toNumber(elementValue, { precision: 2 })
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
  click<ClickFunction extends Function>(
    selector: string,
    f: ClickFunction
  ): void {
    // do an explicit find again to find the frozen headers
    const budgetTables = $(document).find('[data-behaviour="budget-table"]')

    budgetTables.on('click', selector, (event) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      f(event)
    })
  }
}
